import Immutable from 'immutable';

import {
	webapi,
	TabularInputDefinition as TabularInput,
	createModuleState,
	createModelForState,
	REPORTED_EXCEPTION,
	StaticLookup,
	setPreference,
	getPreference,
	EntityDetailModel,
	FormDefinition as Form,
	FormItemDefinition as FormItem,
	DefinitionUtils,
	CrossDefinition as Cross,
	isEmpty,
	ContextValidator,
	ValueValidator,
	ButtonDefinition as Button,
	BoxDefinition as Box,
	ContainerDefinition as Container,
	showMessageBox,
	updateEntityTimestamp,
	styled,
	DateUtils,
	NumberUtils,
	RuntimeUtils,
} from 'ps-gui';
import {
	compareShares,
	createSearchModel,
	getSqlFulltextFilter,
	isrcInputFilter,
	parseRoleId,
	sum,
	validateISRC,
	validateRuntime
} from './utils';
import RecordingProtocolDetailModel from './recordingprotocol/recordingprotocoldetailmodel';
import { getLookup } from '../data/lookup';
import { getPhrase, getFormItemLabel } from '../data/phrases';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_LIST = Immutable.List([]);
const EMPTY_STATE = createModuleState('MyRecordings');

const formatPct = NumberUtils.getFixedDecimalFormatNumber(2);

const PerCatSaveDialog = styled.div`
	min-width: 600px;
	.dialogHeader {
		text-align: center;
		font-weight: 500;
		margin-bottom: 16px;
		color: ${props => props.theme.disabledTextColor};
	}
	.dialogBody {
		max-height: calc(100vh - 240px);
		overflow-y: auto;
	}
	table {
		border-collapse: collapse;
		width: 100%;
		&.blockTable {
			td:first-child {
				padding-left: 32px;
			}
		}
	}
	div.block {
		padding: 8px 16px;
		.blockHeader {
			opacity: 0.7;
			font-weight: 500;
			user-select: all;
			color: ${props => props.theme.disabledTextColor};
			font-size: 14px;
			margin-bottom: 8px;
		}
		&.modified {
			.blockHeader {
				opacity: 1;
			}
		}
	}
	tr {
		&:hover {
			background: ${props => props.theme.tableRowHoverBackground};
		}

		&.deleted {
			opacity: 0.4;
			* {
				text-decoration: line-through;
			}
		}

		td {
			padding: 8px 16px;

			&.label {
				opacity: 0.7;
				font-weight: 500;
				user-select: all;
				color: ${props => props.theme.disabledTextColor};
				font-size: 14px;
			}

			.old {
				text-decoration: line-through;
				margin-right: 8px;
				user-select: all;
				opacity: 0.7;
				font-size: 90%;
			}
			.new {
				font-weight: 700;
				user-select: all;
			}
			.unchanged {
				user-select: all;
			}
		}
		&.modified {
			.label {
				opacity: 1;
			}
		}
	}
`;

const perCatAttributes = [
	'isrc',
	'producer',
	'label',
	'mainArtist',
	'album',
	'title',
	'subtitle',
	'recordingTypeId',
	'genreId',
	'recordingCountryId',
	'releaseYear',
	'productionYear',
	'length',
	/*'licences',
	'performers',
	'composers',
	'lyricists'*/
];

function formatAttribute(attr, value) {
	if (attr === 'recordingTypeId') {
		return getLookup('recordingProtocolRecordingType').getText(value);
	} else if (attr === 'recordingCountryId') {
		return getLookup('country').getText(value);
	} else if (attr === 'genreId') {
		return getLookup('genre').getText(value);
	} else if (attr === 'fromDate' && value) {
		return getFormItemLabel('from.lower') + ' ' + DateUtils.formatDate(value);
	} else if (attr === 'toDate' && value) {
		return getFormItemLabel('to.lower') + ' ' + DateUtils.formatDate(value);
	} else if (attr === 'performance') {
		return getLookup('catalogPerformance').getText(value);
	} else if (attr === 'sharePct' && typeof value === 'number') {
		return formatPct(value) + '%';
	} else if (attr === 'length') {
		return RuntimeUtils.formatRuntime(value);
	}
	return value;
}

function renderTableBlock(headerPhraseId, rows, originalRows, attrs) {
	if (!rows || rows.size === 0) {
		return null;
	}
	originalRows = originalRows || EMPTY_LIST;
	return (
		<div className={'block' + (rows !== originalRows ? ' modified' : ' unmodified')}>
			<div className='blockHeader'>{getPhrase(headerPhraseId)}</div>
			<table className='blockTable'>
				<tbody>
					{
						rows.map((row, rowIndex) => {
							const origRow = originalRows.get(rowIndex) || EMPTY_MAP;
							const deleted = row.get('_deleted');
							return (
								<tr key={rowIndex} className={deleted ? 'deleted' : ''}>
									{
										attrs.map(attr => {
											let oldValue = origRow.get(attr);
											let newValue = row.get(attr);
											const modified = oldValue !== newValue;
											return (
												<td data-name={attr} key={attr}>
													{
														!modified ? (
															<div className='unchanged'>{formatAttribute(attr, newValue)}</div>
														) : (
															<>
																<div className='new'>{formatAttribute(attr, newValue)}</div>
																{!isEmpty(oldValue) && <div className='old'>{formatAttribute(attr, oldValue)}</div>}
															</>
														)
													}
												</td>
											);
										})
									}
								</tr>
							);
						})
					}
				</tbody>
			</table>
		</div>
	);
}

async function onBeforeSavePermanentCatalog(detail, originalDetail) {
	const dlgRes = await showMessageBox(() => {
		return (
			<PerCatSaveDialog>
				<div className='dialogHeader'>{getPhrase('reallySavePermanentCatalogChanges')}</div>
				<div className='dialogBody'>
					<table className='recording'>
						<tbody>
							{
								perCatAttributes.map(attr => {
									let oldValue = originalDetail.get(attr);
									let newValue = detail.get(attr);
									const modified = oldValue !== newValue;
									return (
										<tr key={attr} className={modified ? 'modified' : 'unmodified'}>
											<td className={'label'}>{getFormItemLabel(attr, 'recording')}:</td>
											<td className='value'>
												{
													!modified ? (
														<div className='unchanged'>{formatAttribute(attr, newValue)}</div>
													) : (
														<>
															<div className='new'>{formatAttribute(attr, newValue)}</div>
															{!isEmpty(oldValue) && <div className='old'>{formatAttribute(attr, oldValue)}</div>}
														</>
													)
												}
											</td>
										</tr>
									);
								})
							}
						</tbody>
					</table>
					{renderTableBlock('licences', detail.get('licences'), originalDetail.get('licences'), ['fromDate', 'toDate', 'sharePct'])}
					{renderTableBlock('recordingProtocol.performers', detail.get('performers'), originalDetail.get('performers'), ['name', 'performance', 'sharePct'])}
					{renderTableBlock('recordingProtocol.composers', detail.get('composers'), originalDetail.get('composers'), ['name'])}
					{renderTableBlock('recordingProtocol.lyricists', detail.get('lyricists'), originalDetail.get('lyricists'), ['name'])}
				</div>
			</PerCatSaveDialog>
		);
	}, {type: 'confirm'});
	return dlgRes === 'yes';
}

export default class MyRecordingsModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		if (Array.isArray(name)) {
			return new MyRecordingsModel(this.#data.setIn(name, value));
		} else {
			return new MyRecordingsModel(this.#data.set(name, value));
		}
	}

	#updateTabs() {
		let tabs = baseTabs;
		if (this.#data.get('recordingProtocolDetail')) {
			tabs = tabs.push(Immutable.Map({
				id: 'recordingProtocolDetail',
				text: '#'
			}));
		}
		if (this.#data.get('permanentCatalogDetail')) {
			tabs = tabs.push(Immutable.Map({
				id: 'permanentCatalogDetail',
				text: '#'
			}));
		}
		return this.#setProperty('tabs', tabs);
	}

	closeRecordingProtocolDetail() {
		const recordingProtocolDetailOpenedFromTab = this.#data.get('recordingProtocolDetailOpenedFromTab');
		let model = this.#setProperty('recordingProtocolDetail', null);
		if (recordingProtocolDetailOpenedFromTab) {
			model = model.setSelectedTabId(recordingProtocolDetailOpenedFromTab);
		}
		return model.#updateTabs();
	}

	closePermanentCatalogDetail() {
		let model = this.#setProperty('permanentCatalogDetail', null);
		model = model.setSelectedTabId('registeredRecordings');
		return model.#updateTabs();
	}

	async openPermanentCatalogDetail(producerId, recordingId) {
		DefinitionUtils.setLabels(permanentCatalogDetailDefinition, 'recording');
		let detailModel = new EntityDetailModel()
			.setLoadUri('/api/proc/api_myrecordings/getPermanentCatalogRecordingDetail')
			.setSaveUri('/api/proc/api_myrecordings/savePermanentCatalogRecording')
			.setDefinition(permanentCatalogDetailDefinition)
			.setProcessOnLoad(data => {
				const performers = data.get('performers');
				if (!performers) {
					return data;
				}
				return data.set('performerTotalShare', sum(performers, 'sharePct'));
			})
			.setOnBeforeSave(onBeforeSavePermanentCatalog)
			.setEntityName('recording');

		detailModel = await detailModel.loadDetail(producerId + '/' + recordingId);
		if (!detailModel.getData().getIn(['detail', 'isEditable'])) {
			detailModel = detailModel.setReadOnly(true);
		}
		return this.#setProperty('permanentCatalogDetail', detailModel.getState()).setSelectedTabId('permanentCatalogDetail').#updateTabs();
	}

	openRecordingProtocolDetail() {
		const roles = getLookup('recordingProtocolPartyRole').getItems();
		const hasProducer = roles.some(role => role.get('key').endsWith('PRO'));
		const roleId = this.#data.get('roleId');
		let rpm = new RecordingProtocolDetailModel();
		if (roles.findIndex(r => r.get('key') === roleId) < 0) {
			rpm = rpm.setRoleId(roles.getIn([0, 'key']));
		} else {
			rpm = rpm.setRoleId(roleId);
		}
		return this
			.#setProperty('recordingProtocolDetail', rpm.getState())
			.#setProperty('recordingProtocolDetailOpenedFromTab', this.#data.get('selectedTabId') || 'registeredRecordings')
			.#updateTabs()
			.#setProperty('selectedTabId', 'recordingProtocolDetail');
	}

	async loadRecordingProtocolDetail(id) {
		let rpm = new RecordingProtocolDetailModel();
		rpm = await rpm.load(id);
		return this
			.#setProperty('recordingProtocolDetail', rpm.getState())
			.#setProperty('recordingProtocolDetailOpenedFromTab', this.#data.get('selectedTabId'))
			.#updateTabs()
			.#setProperty('selectedTabId', 'recordingProtocolDetail');
	}

	setSelectedTabId(value) {
		return this.#setProperty('selectedTabId', value);
	}

	setPartyId(partyId) {
		console.log('setPartyId', partyId);
		return this.#setProperty('partyId', partyId);
	}

	async checkGlobalContextChange(globalContext) {
		if (!globalContext) {
			return this;
		}
		const gcLangId = globalContext.getLanguageId();
		const langId = this.#data.get('langId');
		if (gcLangId === langId) {
			return this;
		}
		return (await this.initialize(globalContext)).#setProperty('languageTimestamp', new Date().getTime()).#updateTabs();
	}

	getLanguageTimestamp() {
		return this.#data.get('languageTimestamp') || 0;
	}

	async initializeLookups() {
		const partyId = this.#data.get('partyId');
		let pi = await webapi.execute('/api/proc/api_myrecordings/getpartyinfo', {
			partyId
		});
		if (pi === REPORTED_EXCEPTION) {
			return this;
		}
		console.log('pi = ', pi);
		let model = this.#setProperty('roleLookup', StaticLookup(Immutable.fromJS(pi.roles || [])));
		if (pi.producerDistributionPeriods) {
			for (let roleId in pi.producerDistributionPeriods) {
				let items = pi.producerDistributionPeriods[roleId] || [];
				model = model.#setProperty('distributionPeriodLookup.' + roleId, StaticLookup(Immutable.fromJS(items)));
			}
		}

		const roleId = pi.roles?.[0]?.key;
		return model
			.setRoleId(roleId).#setProperty('tabularSourceProducers', Immutable.fromJS(pi.tabularSourceProducers || []));
	}

	setRoleId(roleId) {
		let model = this.#setProperty('roleId', roleId);
		let dp = this.#data.get('distributionPeriodLookup.' + roleId);
		if (dp) {
			model = model.setDistributionPeriodId(dp.getItems().getIn([0, 'key']));
		}
		return model;
	}

	setDistributionPeriodId(value) {
		return this.#setProperty('distributionPeriodId', value);
	}

	#getPreferenceKeyBase() {
		return '//myrec//';
	}

	#getExtFilterPreferenceKey() {
		return this.#getPreferenceKeyBase() + 'extFilter';
	}

	initializePreferences() {
		let data = this.#data;
		let ef = getPreference(this.#getExtFilterPreferenceKey());
		if (!ef) {
			return this;
		}
		const roleLookup = data.get('roleLookup');
		if (roleLookup) {
			const roleId = ef.get('roleId');
			if (roleId && roleLookup.getText(roleId)) {
				data = data.set('roleId', roleId);
			}
		}
		const distLookup = data.get('distributionPeriodLookup.' + data.get('roleId'));
		if (distLookup) {
			const distPeriodId = ef.get('distributionPeriodId') || distLookup.getItems().getIn([0, 'key']);
			data = data.set('distributionPeriodId', distPeriodId);
		}
		if (ef.get('sources')) {
			data = data.set('sources', ef.get('sources'));
		}
		return new MyRecordingsModel(data);
	}

	async initialize(globalContext) {
		console.log('inicializuji myrecordings');
		let model = await this.initializeLookups();
		model = model.#setProperty('sources', Immutable.Map({
			isrcProtocol: true,
			other: true
		}));
		let srch = createSearchModel('/api/dq/sv_my_recording').setEntityName('recording').setFixedFilter({ creator_id: -1 }).setProcessFilter(processSearchFilter);
		srch = srch.setOutput(getSearchOutputColumns());
		srch = srch.initializePreferences();
		model = model.setSearch(srch);

		srch = createSearchModel('/api/dq/sv_recording_protocol_submission').setProcessFilter(processSearchFilter).setEntityName('recordingProtocol');
		srch = srch.setOutput(getRecordingProtocolSearchOutputColumns());
		srch = srch.initializePreferences();
		model = model.setRecordingProtocolSearch(srch);

		model = model.initializePreferences();
		model = await model.refreshSearch(true);
		return model.#setProperty('langId', globalContext.getLanguageId());
	}

	async refreshSearch(dontSavePreference) {
		const roleId = this.#data.get('roleId');
		if (!roleId) {
			return this;
		}
		const partyRole = parseRoleId(roleId) || {};
		const distributionPeriodId = this.#data.get('distributionPeriodId');
		const sources = this.#data.get('sources') || EMPTY_MAP;
		let model = this;
		let fixedFilter = {};
		if (!dontSavePreference && roleId && distributionPeriodId) {
			setPreference(this.#getExtFilterPreferenceKey(), Immutable.Map({
				roleId,
				distributionPeriodId,
				sources
			}));
		}
		if (partyRole.roleTypeId === 'PRO') {
			fixedFilter.producer_id = partyRole.partyId;
			fixedFilter.producer_distribution_period_id = distributionPeriodId;
			fixedFilter._url = '/api/dq/sv_permanent_catalog_recording';
			model = model.#setProperty('mode', 'producer');
		} else {
			fixedFilter.creator_id = partyRole.partyId;
			fixedFilter.creator_role_type_id = partyRole.roleTypeId;
			fixedFilter._url = '/api/dq/sv_my_recording';
			model = model.#setProperty('mode', 'performer');
		}
		let isIsrcProtocol = '#';
		if (sources.get('isrcProtocol')) {
			if (sources.get('other')) {
				isIsrcProtocol = null;
			} else {
				isIsrcProtocol = 'Y';
			}
		} else if (sources.get('other')) {
			isIsrcProtocol = 'N';
		}
		fixedFilter.is_isrc_protocol = isIsrcProtocol;

		let srch = model.getSearch().setFixedFilter(fixedFilter);
		srch = await srch.search(1);
		return model.setSearch(srch);
	}

	async setSource(name, value) {
		let model = this.#setProperty(['sources', name], value);
		return await model.refreshSearch();
	}

	setSearch(model) {
		return new MyRecordingsModel(this.#data.set('search', model.getState()));
	}

	getSearch() {
		return createModelForState(this.#data.get('search'));
	}

	setRecordingProtocolSearch(model) {
		return new MyRecordingsModel(this.#data.set('recordingProtocolSearch', model.getState()));
	}

	getRecordingProtocolSearch() {
		return createModelForState(this.#data.get('recordingProtocolSearch'));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}
}

function processSearchFilter(filter) {
	if (filter.recording) {
		filter.recording = getSqlFulltextFilter(filter.recording);
	}
	if (filter.main_artist) {
		filter.main_artist = getSqlFulltextFilter(filter.main_artist);
	}
	if (filter.role_id) {
		var pr = parseRoleId(filter.role_id);
		filter.party_id = pr.partyId;
		filter.role_type_id = pr.roleTypeId;
	}
	return filter;
}

export function getSearchOutputColumns(forExport) {
	if (forExport) {
		return [
			'recording_id',
			'title',
			'subtitle',
			'main_artist',
			'production_year',
			'release_year',
			'is_isrc_protocol',
			'length',
			'audio_video',
			'isrc',
			'licence'
		];
	} else {
		return [
			'recording',
			'main_artist',
			'production_release_year',
			'length',
			'audio_video',
			'isrc',
			'licence'
		];
	}
}

export function getRecordingProtocolSearchOutputColumns() {
	return [
		'recording',
		'role_id',
		'main_artist',
		'production_release_year',
		'length',
		'audio_video',
		'isrc',
		'submission_type',
		'submission_status',
		'last_modified'
	];
}

const baseTabs = Immutable.fromJS([{
	id: 'registeredRecordings',
	text: () => getPhrase('registeredRecordings')
}, {
	id: 'recordingProtocols',
	text: () => getPhrase('recordingProtocols')
}]);


const margin = '32px';
const boxPadding = '0px 8px';
const contentPadding = '12px 4px';
const headerPadding = '12px 4px';

const PERFORMER_TOTAL_SHARE_PATH = ['detail', 'performerTotalShare'];

const permanentCatalogDetailDefinition =
	Form().setOpacity(ctx => ctx.getProperty('isDeleted') === 'Y' ? '0.3' : '1').setBasePath('detail').setItems([
		Box().setContent(Container([
			Cross().setItems([
				FormItem('isrc', 'text')
					.setMaxLength(12)
					.setWidth('160px')
					.setInputFilter(isrcInputFilter)
					.onChange(async (ctx, def, value) => {
					})
					.setValidator(ValueValidator(validateISRC)),
				FormItem('producer', 'text').setMaxLength(250),
				FormItem('label', 'text').setMaxLength(250)
			]),
			Cross().setItems([
				FormItem('mainArtist', 'text').setMaxLength(400).setMandatory(true),
				FormItem('album', 'text').setMaxLength(255)
			]),
			Cross().setItems([
				FormItem('title', 'text').setMandatory(true).setMaxLength(255),
				FormItem('subtitle', 'text').setMaxLength(255),
			]),
			Cross().setItems([
				FormItem('recordingTypeId', 'lookup')
					.setLookup('recordingProtocolRecordingType')
					.setMandatory(true),
				FormItem('genreId', 'lookup')
					.setLookup('genre')
					.setMandatory(true),
				FormItem('recordingCountryId', 'lookup').setLookup('country').setWidth('calc( 50% - 7px )')
			]).setValidator(ContextValidator(((errors, ctx, itemName) => {
				if (itemName === 'releaseYear' || itemName === 'productionYear' /*|| itemName === 'publicationYear'*/ || !itemName) {
					const fullPath = ctx.getFullPath('releaseYear');
					const aloSet = ctx.getProperty('releaseYear') || ctx.getProperty('productionYear') /*|| ctx.getProperty('publicationYear')*/;
					errors.add(fullPath, {
						type: 'error',
						code: 'invalidValue',
						message: aloSet ? null : getPhrase('releaseOrProductionYearIsMandatory')
					});
				}
			}))),
			Cross().setItems([
				FormItem('releaseYear', 'lookup').setLookup('year'),
				FormItem('productionYear', 'lookup').setLookup('year'),
				// FormItem('publicationYear', 'lookup').setLookup('year'),
				FormItem('length', 'runtime').setValidator(ContextValidator(((errors, ctx, itemName) => {
					if (itemName === 'length' || !itemName) {
						const value = ctx.getProperty('length');
						const fullPath = ctx.getFullPath('length');
						if (isEmpty(value)) {
							errors.add(
								fullPath,
								{
									type: 'error',
									code: 'mandatoryField',
									longMessage: getFormItemLabel('length', 'recording') + getPhrase('isAMandatoryField'),
									message: getPhrase('mandatoryField')
								}
							);
						} else {
							errors.clear(fullPath, 'mandatoryField');
						}
						errors.add(fullPath, {
							type: 'error',
							code: 'invalidValue',
							message: validateRuntime(value)
						});

					}
				}))),
			]),
			TabularInput().setMarginTop('32px').setMainHeader(() => getPhrase('licences')).setPath('licences').setItems([
				FormItem('fromDate', 'date'),
				FormItem('toDate', 'date'),
				FormItem('sharePct', 'number').setMaxDecimals(4).setWidth('120px'),
			]).setRowListValidator(ContextValidator((errors, ctx, itemName) => {
			})),
			TabularInput().setMarginTop('32px').setMainHeader(() => getPhrase('recordingProtocol.performers')).setPath('performers').setItems([
				FormItem('name', 'text').setMaxLength(250).setMandatory(true),
				FormItem('performance', 'lookup').setLookup('catalogPerformance'),
				FormItem('sharePct', 'number').setMaxDecimals(4).setWidth('120px'),
			]).setVisible(ctx => ctx.getRootProperty(['detail', 'hasEditablePerformers'])).setRowListValidator(ContextValidator((errors, ctx, itemName) => {
				// jsme zavisli na zmenach udelanych v onChange, takze aktualizujeme context, aby videl provedene zmeny
				ctx.updateData(true);
				const path = PERFORMER_TOTAL_SHARE_PATH;
				const totalShare = ctx.getRootProperty(path);
				const cmp = typeof totalShare === 'number' ? compareShares(100, totalShare) : null;
				errors.add(path, cmp ? getPhrase('totalShareMustBe100') : null, 'totalShare');
				if (!itemName) {
					const rows = ctx.getData();
					errors.add(['detail', 'aloPerformer'], !rows || rows.filter(r => !r.get('_deleted')).size === 0 ? getPhrase('recordingProtocol.aloPerormer') : null, 'aloPerformer');
				}
			})).onChange((ctx, rowIndex, item) => {
				// console.log(item.getName(), ctx.getProperty([rowIndex, item.getName()]), ctx.getData().toJS());
				ctx.setRootProperty(PERFORMER_TOTAL_SHARE_PATH, sum(ctx.getData(), 'sharePct'));
			}),
			TabularInput().setMarginTop('32px').setMainHeader(() => getPhrase('recordingProtocol.performers')).setPath('performers').setItems([
				FormItem('name', 'text').setMaxLength(250).setMandatory(true),
				FormItem('sharePct', 'number').setMaxDecimals(4).setWidth('140px'),
			]).setReadOnly(true).setVisible(ctx => !ctx.getRootProperty(['detail', 'hasEditablePerformers'])),
			Cross().setItems([
				FormItem('performerTotalShare', 'number').setReadOnly(true).setWidth('140px')
			]).setJustifyContent('right').setVisible(ctx => typeof ctx.getProperty('performerTotalShare') === 'number'),
			Cross().setItems([
				TabularInput().setKeySuffix(ctx => ctx.getRootProperty(['detail', 'genreId'])).setMarginTop('32px').setMainHeader(() => getPhrase('recordingProtocol.composers')).setPath('composers').setItems([
					FormItem('name', 'text').setMaxLength(250).setMandatory(true),
				]).setMainError(ctx => {
					const genreId = ctx.getRootProperty(['detail', 'genreId']);
					if (genreId !== 'CLS') {
						return null;
					}
					const composers = ctx.getRootProperty(['detail', 'composers']);

					if (!composers || composers.some(c => !c.get('_deleted'))) {
						return null;
					}
					return getPhrase('recordingProtocolCLSMissingComposer');
				}),
				TabularInput().setMarginTop('32px').setMainHeader(() => getPhrase('recordingProtocol.lyricists')).setPath('lyricists').setItems([
					FormItem('name', 'text').setMaxLength(250).setMandatory(true),
				]),
			]),
			Cross().setItems([
				Button('deleteRecording', () => getPhrase('removeRecordingFromCatalog')).setType('medium').setMargin('32px 0px').setListener(async e => {
					const ctx = e.ctx;
					const dlgRes = await showMessageBox(getPhrase('reallyRemoveRecordingFromCatalog'), {
						type: 'confirm',
						yesIsNegative: true
					});
					if (dlgRes !== 'yes') {
						return;
					}
					const res = await webapi.execute('/api/proc/api_myrecordings/deletePermanentCatalogRecording', {
						id: ctx.getProperty('id')
					});
					if (res === REPORTED_EXCEPTION) {
						return;
					}
					ctx.setModel(await ctx.getModel().reload());
					updateEntityTimestamp('recording');
				}).setVisible(ctx => ctx.getProperty('isDeleted') !== 'Y')
			]).setJustifyContent('right'),
		])).setMargin(margin).setBoxPadding(boxPadding).setPadding(contentPadding).setHeaderPadding(headerPadding)
	]);

